export const ExamTaskCategory = {
    Speaking: 'Speaking',
    Writing: 'Writing',
    Reading: 'Reading',
    Listening: 'Listening',
} as const;

export type ExamTaskCategory = (typeof ExamTaskCategory)[keyof typeof ExamTaskCategory];

export type ExamName = 'Academic' | 'Core';

export type ExamTaskType = {
    key: string;
    label: string;
    category: ExamTaskCategory;
    apiType: string;
    examNames: ExamName[];
    isVoiceRequired: boolean;
    aliases?: string[];
    labelsByExamName?: Partial<Record<ExamName, string>>;
};

function slugify(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/\(.*?\)/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

function makeKey(category: ExamTaskCategory, label: string): string {
    return `${category.toLowerCase()}-${slugify(label)}`;
}

export const examNames: ExamName[] = ['Academic', 'Core'];

export const examTaskCategories: Array<{ id: ExamTaskCategory; label: string }> = [
    { id: ExamTaskCategory.Speaking, label: 'Speaking' },
    { id: ExamTaskCategory.Writing, label: 'Writing' },
    { id: ExamTaskCategory.Reading, label: 'Reading' },
    { id: ExamTaskCategory.Listening, label: 'Listening' },
];

export const examTaskTypes: ExamTaskType[] = [
    { category: ExamTaskCategory.Speaking, label: 'Read Aloud', key: makeKey(ExamTaskCategory.Speaking, 'Read Aloud'), apiType: 'ReadAloud', examNames: ['Academic', 'Core'], isVoiceRequired: false },
    { category: ExamTaskCategory.Speaking, label: 'Repeat Sentence', key: makeKey(ExamTaskCategory.Speaking, 'Repeat Sentence'), apiType: 'RepeatSentence', examNames: ['Academic', 'Core'], isVoiceRequired: true },
    { category: ExamTaskCategory.Speaking, label: 'Describe Image', key: makeKey(ExamTaskCategory.Speaking, 'Describe Image'), apiType: 'DescribeImage', examNames: ['Academic', 'Core'], isVoiceRequired: false },
    {
        category: ExamTaskCategory.Speaking,
        label: 'Re-tell Lecture',
        key: makeKey(ExamTaskCategory.Speaking, 'Re-tell Lecture'),
        apiType: 'RetellLecture',
        examNames: ['Academic'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Speaking, 'Retell Lecture')],
    },
    {
        category: ExamTaskCategory.Speaking,
        label: 'Answer Short Questions',
        key: makeKey(ExamTaskCategory.Speaking, 'Answer Short Questions'),
        apiType: 'AnswerShortQuestion',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Speaking, 'Answer Short Question')],
    },
    { category: ExamTaskCategory.Speaking, label: 'Summarize Group Discussion', key: makeKey(ExamTaskCategory.Speaking, 'Summarize Group Discussion'), apiType: 'SummarizeGroupDiscussion', examNames: ['Academic'], isVoiceRequired: true },
    {
        category: ExamTaskCategory.Speaking,
        label: 'Respond to a Situation',
        key: makeKey(ExamTaskCategory.Speaking, 'Respond to a Situation'),
        apiType: 'RespondToSituation',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Speaking, 'Respond to Situation')],
        labelsByExamName: {
            Academic: 'Respond to a Situation',
            Core: 'Respond to Situation',
        },
    },
    { category: ExamTaskCategory.Writing, label: 'Summarize Written Text', key: makeKey(ExamTaskCategory.Writing, 'Summarize Written Text'), apiType: 'SummarizeWrittenText', examNames: ['Academic', 'Core'], isVoiceRequired: false },
    { category: ExamTaskCategory.Writing, label: 'Write Essay', key: makeKey(ExamTaskCategory.Writing, 'Write Essay'), apiType: 'WriteEssay', examNames: ['Academic'], isVoiceRequired: false },
    { category: ExamTaskCategory.Writing, label: 'Write Email', key: makeKey(ExamTaskCategory.Writing, 'Write Email'), apiType: 'WriteEmail', examNames: ['Core'], isVoiceRequired: false },
    {
        category: ExamTaskCategory.Reading,
        label: 'Fill in the Blanks Reading & Writing',
        key: makeKey(ExamTaskCategory.Reading, 'Fill in the Blanks Reading & Writing'),
        apiType: 'RwFib',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: false,
        aliases: [makeKey(ExamTaskCategory.Reading, 'Fill in the Blanks (Dropdown)')],
    },
    {
        category: ExamTaskCategory.Reading,
        label: 'Multiple Type, Double Answer',
        key: makeKey(ExamTaskCategory.Reading, 'Multiple Type, Double Answer'),
        apiType: 'ReadingMcqMultiple',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: false,
        aliases: [makeKey(ExamTaskCategory.Reading, 'Multiple Choice (Multiple)')],
    },
    { category: ExamTaskCategory.Reading, label: 'Reorder Paragraph', key: makeKey(ExamTaskCategory.Reading, 'Reorder Paragraph'), apiType: 'ReadingReorder', examNames: ['Academic', 'Core'], isVoiceRequired: false },
    {
        category: ExamTaskCategory.Reading,
        label: 'Reading Fill in the Blanks',
        key: makeKey(ExamTaskCategory.Reading, 'Reading Fill in the Blanks'),
        apiType: 'ReadingDragDrop',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: false,
        aliases: [makeKey(ExamTaskCategory.Reading, 'Fill in the Blanks (Drag and Drop)')],
    },
    {
        category: ExamTaskCategory.Reading,
        label: 'Multiple Type, Single Answer',
        key: makeKey(ExamTaskCategory.Reading, 'Multiple Type, Single Answer'),
        apiType: 'ReadingMcqSingle',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: false,
        aliases: [makeKey(ExamTaskCategory.Reading, 'Multiple Choice (Single)')],
    },
    { category: ExamTaskCategory.Listening, label: 'Summarize Spoken Text', key: makeKey(ExamTaskCategory.Listening, 'Summarize Spoken Text'), apiType: 'SummarizeSpokenText', examNames: ['Academic', 'Core'], isVoiceRequired: true },
    {
        category: ExamTaskCategory.Listening,
        label: 'MCQ Multiple Answer',
        key: makeKey(ExamTaskCategory.Listening, 'MCQ Multiple Answer'),
        apiType: 'ListeningMcqMultiple',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Listening, 'Multiple Choice (Multiple)')],
    },
    {
        category: ExamTaskCategory.Listening,
        label: 'Listening Fill in the Blanks',
        key: makeKey(ExamTaskCategory.Listening, 'Listening Fill in the Blanks'),
        apiType: 'ListeningFillinTheBlanks',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Listening, 'Fill in the Blanks')],
    },
    { category: ExamTaskCategory.Listening, label: 'Highlight Correct Summary', key: makeKey(ExamTaskCategory.Listening, 'Highlight Correct Summary'), apiType: 'ListeningHighlightSummary', examNames: ['Academic'], isVoiceRequired: true },
    {
        category: ExamTaskCategory.Listening,
        label: 'MCQ Single Answer',
        key: makeKey(ExamTaskCategory.Listening, 'MCQ Single Answer'),
        apiType: 'ListeningMcqSingle',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Listening, 'Multiple Choice (Single)')],
    },
    { category: ExamTaskCategory.Listening, label: 'Select Missing Word', key: makeKey(ExamTaskCategory.Listening, 'Select Missing Word'), apiType: 'ListeningSelectMissingWord', examNames: ['Academic', 'Core'], isVoiceRequired: true },
    {
        category: ExamTaskCategory.Listening,
        label: 'Highlight Incorrect Word',
        key: makeKey(ExamTaskCategory.Listening, 'Highlight Incorrect Word'),
        apiType: 'ListeningHighlightIncorrectWord',
        examNames: ['Academic', 'Core'],
        isVoiceRequired: true,
        aliases: [makeKey(ExamTaskCategory.Listening, 'Highlight Incorrect Words')],
    },
    { category: ExamTaskCategory.Listening, label: 'Write from Dictation', key: makeKey(ExamTaskCategory.Listening, 'Write from Dictation'), apiType: 'ListeningWriteFromDictation', examNames: ['Academic', 'Core'], isVoiceRequired: true },
];

export function getExamTaskTypeByKey(key?: string) {
    return examTaskTypes.find(
        (examTaskType) => examTaskType.key === key || examTaskType.aliases?.includes(key ?? ''),
    );
}

export function getExamTaskTypeByApiType(apiType?: string) {
    return examTaskTypes.find((examTaskType) => examTaskType.apiType === apiType);
}

export function isVoiceRequiredExamTaskType(apiType?: string) {
    return Boolean(getExamTaskTypeByApiType(apiType)?.isVoiceRequired);
}

export function getExamTaskTypesByExamName(examName: ExamName) {
    return examTaskTypes.filter((examTaskType) => examTaskType.examNames.includes(examName));
}

export function getExamTaskTypeLabel(examTaskType: ExamTaskType | null | undefined, examName?: ExamName) {
    if (!examTaskType) {
        return undefined;
    }

    if (examName) {
        const label = examTaskType.labelsByExamName?.[examName];
        if (label) {
            return label;
        }
    }

    return examTaskType.label;
}

export function isExamTaskTypeAvailableForExamName(key: string | undefined, examName: ExamName) {
    const examTaskType = getExamTaskTypeByKey(key);
    return Boolean(examTaskType && examTaskType.examNames.includes(examName));
}

export function getDefaultExamTaskTypeForExamName(examName: ExamName) {
    return getExamTaskTypesByExamName(examName)[0] ?? null;
}